import { useCallback, useEffect } from "react";
import {
  bootStatusSelector,
  isAuthenticatedSelector,
  loadingAuthSelector,
  userSelector,
  type AuthBootStatus,
} from "../logic/Auth/authSlice";
import { fetchUserInformation, logoutUser, refreshUserInformation } from "../logic/Auth/auth.thunk";
import IUser from "../logic/Auth/IUser";
import { useCustomDispatch, useCustomSelector } from "./hooks";
import { ROLES } from "../utils/enum";

export interface IAuthenticationHook {
  user: IUser | null;
  isAuthenticated: boolean;
  refresh: () => Promise<void>;
  logout: () => void;
  isAdmin: () => boolean;
  loadingAuth: boolean;
  /** Boot lifecycle. `"ready"` means the initial `/me` call has resolved. */
  bootStatus: AuthBootStatus;
}

/**
 * Auth bootstrap + accessor hook. Mounts once at the app root (via
 * `Page.tsx`) and triggers the initial `/me` fetch when the boot
 * status is still `"idle"`. Pre-Phase-5 this used a 3-state
 * `initialFetchStatus: boolean | null` flag for the same purpose;
 * see `authSlice.ts` for the rename rationale.
 */
export default function useAuth(): IAuthenticationHook {
  const dispatch = useCustomDispatch();
  const user = useCustomSelector(userSelector);
  const isAuthenticated = useCustomSelector(isAuthenticatedSelector);
  const bootStatus = useCustomSelector(bootStatusSelector);
  const loadingAuth = useCustomSelector(loadingAuthSelector);

  useEffect(() => {
    if (bootStatus === "idle") {
      // Boot fetch. The thunk handles its own errors; we ignore the
      // returned promise rather than swallow exceptions silently.
      void dispatch(fetchUserInformation());
    }
  }, [dispatch, bootStatus]);

  const refresh = useCallback(async () => {
    try {
      await dispatch(refreshUserInformation()).unwrap();
      await dispatch(fetchUserInformation()).unwrap();
    } catch {
      // Refresh failure routes through the axios interceptor →
      // logout. Nothing to do here.
    }
  }, [dispatch]);

  const logout = useCallback(() => {
    dispatch(logoutUser());
  }, [dispatch]);

  const isAdmin = useCallback((): boolean => Boolean(user && user.roleId === ROLES.ADMIN), [user]);

  return {
    user,
    isAuthenticated,
    refresh,
    logout,
    isAdmin,
    loadingAuth,
    bootStatus,
  };
}
